import { isAnyOf } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { aiBaseQuery } from '../base/baseApi';


const task = (builder, url, { withProvider = false } = {}) => builder.mutation({
    query: (body) => ({ url, method: 'POST', body, withProvider }),
});

export const aiApi = createApi({
    reducerPath: 'aiApi',
    baseQuery: aiBaseQuery,
    endpoints: (builder) => ({
        transcribeAudio: task(builder, '/landing/transcribe'), // body: FormData with `file`
        generateMarketSnapshot: task(builder, '/landing/market-snapshot'),

        // Business info (onboarding + dashboard)
        suggestCategories: task(builder, '/business/category-suggestions'),
        suggestBusinessNames: task(builder, '/business/name-suggestions'),

        // Setup questionnaire
        suggestQuestionAnswers: task(builder, '/setup/question-suggestions'),
        suggestExpenseCategories: task(builder, '/setup/expense-categories'),
        generateCustomerForecast: task(builder, '/setup/customer-forecast'),

        // Business setup pipeline
        suggestMarketSegments: task(builder, '/business-setup/market-analysis/segments', { withProvider: true }),
        generateMarketAnalysis: task(builder, '/business-setup/market-analysis', { withProvider: true }),
        suggestMoreCompetitors: task(builder, '/business-setup/market-analysis/competitors', { withProvider: true }),
        generateMarketReport: task(builder, '/business-setup/market-analysis/report', { withProvider: true }),
        generateBusinessModel: task(builder, '/business-setup/business-model', { withProvider: true }),
        generateRevenueAssumptions: task(builder, '/business-setup/revenue-forecast/assumptions', { withProvider: true }),
        researchCompetitorPricing: task(builder, '/business-setup/revenue-forecast/competitor-pricing', { withProvider: true }),
        generateFundingPlan: task(builder, '/business-setup/funding-plan', { withProvider: true }),
        generateExitStrategy: task(builder, '/business-setup/exit-strategy', { withProvider: true }),
    }),
});

// Shared loading state for the business-setup steps:
// useGenerateFundingPlanMutation({ fixedCacheKey: AI_STEP_CACHE_KEYS.fundingPlan })
export const AI_STEP_CACHE_KEYS = {
    marketAnalysis: 'ai/market-analysis',
    businessModel: 'ai/business-model',
    revenueForecast: 'ai/revenue-forecast',
    fundingPlan: 'ai/funding-plan',
    exitStrategy: 'ai/exit-strategy',
};

const endpoints = Object.values(aiApi.endpoints);

/** Matches the fulfilled action of any aiApi endpoint. Payload: `{ data, meta }`. */
export const isAiFulfilled = isAnyOf(...endpoints.map((endpoint) => endpoint.matchFulfilled));

/** Matches the rejected action of any aiApi endpoint. Payload: normalized AI error (see base/baseApi). */
export const isAiRejected = isAnyOf(...endpoints.map((endpoint) => endpoint.matchRejected));

export const {
    useTranscribeAudioMutation,
    useGenerateMarketSnapshotMutation,
    useSuggestCategoriesMutation,
    useSuggestBusinessNamesMutation,
    useSuggestQuestionAnswersMutation,
    useSuggestExpenseCategoriesMutation,
    useGenerateCustomerForecastMutation,
    useSuggestMarketSegmentsMutation,
    useGenerateMarketAnalysisMutation,
    useSuggestMoreCompetitorsMutation,
    useGenerateMarketReportMutation,
    useGenerateBusinessModelMutation,
    useGenerateRevenueAssumptionsMutation,
    useResearchCompetitorPricingMutation,
    useGenerateFundingPlanMutation,
    useGenerateExitStrategyMutation,
} = aiApi;
